import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../../utils/axios';
import { OrgUserResponse } from '../../../@types/organizationUser/organizationUser.type';

interface AdminUsersState {
  users: {
    data: OrgUserResponse[];
    loading: boolean;
    error: string | null;
  };
  cities: {
    data: any[];
    loading: boolean;
    error: string | null;
  };
  update: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  create: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
}

const initialState: AdminUsersState = {
  users: {
    data: [],
    loading: false,
    error: null,
  },
  cities: {
    data: [],
    loading: false,
    error: null,
  },
  update: {
    loading: false,
    error: null,
    success: false,
  },
  create: {
    loading: false,
    error: null,
    success: false,
  },
};

// ---------- Thunks ----------

// Fetch all users
export const fetchAllUsers = createAsyncThunk(
  'adminUsers/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/v1/users');
      return response.data as OrgUserResponse[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

// Fetch all cities
export const fetchCities = createAsyncThunk(
  'adminUsers/fetchCities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/v1/cities');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cities');
    }
  }
);

// Register user by Super Admin
export const createUserByAdmin = createAsyncThunk(
  'adminUsers/createUserByAdmin',
  async (userData: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/v1/users/superAdmin/register', userData);
      return response.data as OrgUserResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create user');
    }
  }
);

// Update user by Super Admin
export const updateUserByAdmin = createAsyncThunk(
  'adminUsers/updateByAdmin',
  async (userData: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put('/api/v1/users/superAdmin/update', userData);
      return response.data as OrgUserResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user');
    }
  }
);

const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {
    resetUpdateStatus: (state) => {
      state.update.success = false;
      state.update.error = null;
    },
    resetCreateStatus: (state) => {
      state.create.success = false;
      state.create.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch All
    builder.addCase(fetchAllUsers.pending, (state) => {
      state.users.loading = true;
      state.users.error = null;
    });
    builder.addCase(fetchAllUsers.fulfilled, (state, action) => {
      state.users.loading = false;
      state.users.data = action.payload;
    });
    builder.addCase(fetchAllUsers.rejected, (state, action) => {
      state.users.loading = false;
      state.users.error = action.payload as string;
    });

    // Fetch Cities
    builder.addCase(fetchCities.pending, (state) => {
      state.cities.loading = true;
      state.cities.error = null;
    });
    builder.addCase(fetchCities.fulfilled, (state, action) => {
      state.cities.loading = false;
      state.cities.data = action.payload;
    });
    builder.addCase(fetchCities.rejected, (state, action) => {
      state.cities.loading = false;
      state.cities.error = action.payload as string;
    });

    // Create
    builder.addCase(createUserByAdmin.pending, (state) => {
      state.create.loading = true;
      state.create.error = null;
      state.create.success = false;
    });
    builder.addCase(createUserByAdmin.fulfilled, (state, action) => {
      state.create.loading = false;
      state.create.success = true;
      state.users.data.push(action.payload);
    });
    builder.addCase(createUserByAdmin.rejected, (state, action) => {
      state.create.loading = false;
      state.create.error = action.payload as string;
      state.create.success = false;
    });

    // Update
    builder.addCase(updateUserByAdmin.pending, (state) => {
      state.update.loading = true;
      state.update.error = null;
      state.update.success = false;
    });
    builder.addCase(updateUserByAdmin.fulfilled, (state, action) => {
      state.update.loading = false;
      state.update.success = true;
      const index = state.users.data.findIndex((u) => u.id === action.payload.id);
      if (index !== -1) {
        state.users.data[index] = action.payload;
      }
    });
    builder.addCase(updateUserByAdmin.rejected, (state, action) => {
      state.update.loading = false;
      state.update.error = action.payload as string;
      state.update.success = false;
    });
  },
});

export const { resetUpdateStatus, resetCreateStatus } = adminUsersSlice.actions;
export default adminUsersSlice.reducer;
